import React, { useState } from "react";
import { Briefcase, MapPin, Clock, Mail, Copy, Check } from "lucide-react";

const POSITIONS = [
  {
    title: "Crypto Content Writer / Researcher",
    type: "Remote · Freelance / Part-time",
    desc: "Research and write market analysis, breaking news coverage, and in-depth explainer content for the MediaCrypto Research Desk. You'll cover Bitcoin, Ethereum, altcoins, regulatory developments, and emerging market narratives, working from primary sources like regulatory filings, on-chain data, and official disclosures rather than simply rewriting other outlets' coverage.",
    requirements: [
      "Strong understanding of crypto markets, DeFi, and blockchain fundamentals",
      "Clear, accurate writing with the ability to explain complex topics simply",
      "Comfortable citing sources and verifying claims before publishing",
      "Self-directed — able to pitch and research your own story ideas",
    ],
  },
  {
    title: "Community Manager",
    type: "Remote · Part-time",
    desc: "Grow and moderate the MediaCrypto community. You'll run our monthly prediction competitions and Trading Simulator challenges, engage users on daily discussions, help resolve disputes, and work with us to turn the community into a genuine daily habit rather than a one-time visit.",
    requirements: [
      "Genuine interest in crypto markets and trading communities",
      "Comfortable moderating discussions and enforcing guidelines fairly",
      "Experience running or growing an online community is a plus",
      "Reliable daily presence, even if just checking in briefly",
    ],
  },
  {
    title: "Frontend Developer (React)",
    type: "Remote · Freelance / Contract",
    desc: "Build new tools and features across the platform using React and Tailwind CSS — everything from real-time price charts to new calculators and dashboards. You'll work directly with live market data APIs and help shape the product as MediaCrypto's tool suite grows.",
    requirements: [
      "Solid experience with React and modern JavaScript",
      "Comfortable with Tailwind CSS and building responsive, mobile-friendly UI",
      "Experience with charting libraries or real-time data is a strong plus",
      "Attention to detail — clean, working code over quick hacks",
    ],
  },
];

function EmailReveal() {
  const [copied, setCopied] = useState(false);
  const email = "contact@mediacrypto.ai";

  const copy = () => {
    navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="flex items-center gap-2 mt-5 bg-[var(--bg-elevated)] border border-[var(--border-soft)] rounded-full pl-4 pr-2 py-2 w-fit">
      <Mail className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" />
      <span className="text-sm text-[var(--text-primary)] font-mono">{email}</span>
      <button
        onClick={copy}
        className="flex items-center gap-1 bg-yellow-400 text-black font-bold uppercase tracking-wide rounded-full px-3 py-1.5 hover:bg-yellow-500 transition text-xs"
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

export default function Careers() {
  return (
    <div className="pt-24 pb-20 max-w-3xl mx-auto px-6">
      <div className="text-center mb-12">
        <Briefcase className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
        <h1 className="font-display font-bold text-4xl text-[var(--text-primary)]">Careers at MediaCrypto</h1>
        <p className="mt-4 text-[var(--text-secondary)] leading-relaxed max-w-xl mx-auto">
          We're building a real-time crypto intelligence platform and are always open to hearing from people who want to help shape it.
        </p>
      </div>

      <div className="space-y-5">
        {POSITIONS.map((p) => (
          <div key={p.title} className="bg-[var(--bg-panel)] border border-[var(--border-soft)] rounded-xl p-6">
            <h2 className="font-display font-bold text-xl text-[var(--text-primary)]">{p.title}</h2>
            <div className="flex items-center gap-4 mt-2 text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-wide">
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Remote</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {p.type.split("· ")[1]}</span>
            </div>
            <p className="mt-4 text-sm text-[var(--text-secondary)] leading-relaxed">{p.desc}</p>

            <div className="mt-4">
              <h3 className="text-xs font-mono uppercase tracking-widest text-[var(--text-tertiary)] mb-2">What we're looking for</h3>
              <ul className="space-y-1.5">
                {p.requirements.map((r, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[var(--text-secondary)]">
                    <span className="text-yellow-400 flex-shrink-0">+</span> {r}
                  </li>
                ))}
              </ul>
            </div>

            <p className="text-xs text-[var(--text-tertiary)] mt-4">
              To apply, email us with the position title and a short note about yourself:
            </p>
            <EmailReveal />
          </div>
        ))}
      </div>

      <p className="text-xs text-[var(--text-tertiary)] text-center mt-10">
        Don't see a role that fits? Reach out anyway — we're always interested in hearing from people who want to contribute.
      </p>
    </div>
  );
}